import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { GetStaticProps } from 'next';
import Page from '@/components/Page/Page';
import { getPageContentByPath } from '@/contentful/api/contentfulClientApi';
import { getSunrunShopProductPageContentByPath } from '@/contentful/api/enhancePages/generateProductPage';
import { isDevServer, isPreviewServer } from '@/contentful/api/utils';
import {
  type PageContent,
  PageContentType,
} from '@/contentful/contentful-exports';
import { getPagesByPathSingletonDb } from '@/contentful/lmdb/getPagesByPathSingletonDb';
import { isBlogCategoryType } from '@/utils/blog';
import { NextOutput } from '@/utils/nextOutput';
import { normalizePath } from '@/utils/paths/normalizePath';

interface Props {
  pageContent: PageContent;
  preview: boolean;
}

export default function Path({ pageContent, preview }: Props) {
  let updatedPageContent = pageContent;
  if (preview && !isBlogCategoryType(pageContent.fields.pageContent)) {
    // biome-ignore lint/correctness/useHookAtTopLevel: Automated migration to Biome
    updatedPageContent = useContentfulLiveUpdates(pageContent);
  }

  return <Page content={updatedPageContent} />;
}

export const getStaticPaths = async () => {
  const isPreviewMode = process.env.NEXT_OUTPUT === NextOutput.SERVER;

  if (isPreviewMode) {
    return { paths: [], fallback: 'blocking' };
  }

  const db = getPagesByPathSingletonDb();
  const paths = await db.getKeys().asArray;

  console.log(`[cf_sync]: ${paths.length} static paths are ready.`);
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  const requestedPath = Array.isArray(params?.path)
    ? `/${params.path.join('/')}`
    : '/';

  if (isDevServer() || isPreviewServer()) {
    const entry: PageContent = await getPageContentByPath(requestedPath, true);

    return {
      props: {
        pageContent: entry,
        preview: true,
      },
      // required so server doesn't cache content (Reset cache 1 sec)
      revalidate: 1,
    };
  }

  const db = getPagesByPathSingletonDb();

  if (db.getKeysCount() < 1) {
    throw Error('[cf_sync]: cache is empty');
  }

  const match = db.get(normalizePath(requestedPath));

  if (!match) {
    console.error(`[cf_sync]: no match found for path - ${requestedPath}`);
    return { notFound: true };
  }

  const pageContent = match as PageContent;

  const subType = pageContent?.fields?.pageContent?.sys?.contentType?.sys?.id as
    | PageContentType
    | undefined;

  const pageContentId = pageContent?.fields?.pageContent?.sys?.id;
  if (subType === PageContentType.SUNRUN_SHOP_PRODUCT) {
    console.log(
      `[shop] Fetching fresh shopify GraphQL data for ${requestedPath}`
    );
    const freshShopData = await getSunrunShopProductPageContentByPath(
      requestedPath,
      pageContentId,
      preview
    );
    if (!freshShopData) {
      console.warn(`[shop] No data found for ${requestedPath}`);
      return { notFound: true };
    }

    return {
      props: {
        pageContent: freshShopData,
        preview: preview || false,
      },
    };
  }

  if (!pageContent?.fields) {
    console.error(`[cache] Invalid or missing fields for ${requestedPath}`);
    return { notFound: true };
  }

  return {
    props: {
      pageContent,
      preview: preview || false,
    },
  };
};
